import {
  type SendData,
  type CollabStateBridge,
} from '@atlassian/collab-connection-client';
import { uuidv4 } from 'lib0/random.js';

// todo: https://product-fabric.atlassian.net/browse/CNS-10273 - Remove dependency
// eslint-disable-next-line no-restricted-imports
import { currentSessionId } from '@canvas/canvas-state/analytics';
import { createErrorClass } from '@canvas/error';
import { MessageType } from '@canvas/protocol-types';
import { encodeMessageV2 } from '@canvas/protocol/v2';
import {
  CapabilityTier,
  captureException,
  CustomerImpact,
  EventClassification,
} from '@canvas/sentry-react';
import { getUpdateMessageOrigin } from '@canvas/transaction';

import { mergeUpdate } from '../util/merge-update.ts';

import { type Update, type AbstractBatchedUpdates } from './abstract-batched-updates.ts';
import { debugLog, debugWarn } from './debug-logger.ts';
import { type UpdateMetadata } from './yjs-socket.ts';

const STATE_BRIDGE_BATCH_TIMEOUT_MS = 200;

const StateBridgeBufferMergedNotice = createErrorClass('StateBridgeBufferMergedNotice');

/**
 * The Analytics Web Client exposes session IDs as decimal strings, while the
 * wire format carries the integer. A value outside that format is omitted
 * rather than sent, so a format change upstream cannot put `NaN` on the wire.
 */
function toWireSessionId(sessionId: string | undefined): number | undefined {
  if (sessionId === undefined || !/^\d+$/.test(sessionId)) {
    return undefined;
  }
  const value = Number(sessionId);
  return Number.isSafeInteger(value) ? value : undefined;
}

export class StateBridge
  implements
    CollabStateBridge<Uint8Array>,
    AbstractBatchedUpdates<Uint8Array<ArrayBuffer>, UpdateMetadata>
{
  // stores all pending changes
  private buffer: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>[] = [];
  // stores the current pending outgoing updates
  private outgoing = 0;
  private outgoingId?: string;
  private observer?: (data: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>) => void;
  private flushObserver?: () => void;
  private confirmedPayloadObserver?: (payload: Uint8Array<ArrayBuffer>) => void;
  private mismatchObserver?: () => void;
  private lastSentClock: number | null = null;
  private outgoingPayload?: Uint8Array<ArrayBuffer>;
  private batchTimer: ReturnType<typeof setTimeout> | undefined;
  private destroyed = false;

  constructor(private timeout: number | (() => number) = STATE_BRIDGE_BATCH_TIMEOUT_MS) {}

  setBatchInterval(getBatchIntervalMs?: () => number): void {
    this.timeout = getBatchIntervalMs ?? STATE_BRIDGE_BATCH_TIMEOUT_MS;
  }

  addUpdate(update: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>): void {
    if (this.destroyed) {
      return;
    }

    const bufferSizeBefore = this.buffer.length;
    this.buffer.push(update);
    debugLog(
      '[StateBridge:addUpdate] buffer size before=%d → after=%d, payload bytes=%d, clock=%d, hasObserver=%s',
      bufferSizeBefore,
      this.buffer.length,
      update.payload.byteLength,
      update.clock,
      Boolean(this.observer),
    );
    if (this.outgoing === 0) {
      this.scheduleUpdate();
    }
  }

  flush() {
    if (this.destroyed) {
      return;
    }

    this.flushPendingBatch();

    if (this.buffer.length > 0) {
      this.flushObserver?.();
    }
  }

  // this will set observer to be a function that eventually calls c3.sendThenConfirm (through the abstract Transport layer)
  onUpdate(
    observer: (data: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>) => void,
  ): this {
    this.observer = observer;
    // Updates buffered while no observer was attached (the NCS connect flow detaches
    // it until the namespace join completes) would otherwise wait for the next local
    // edit before anything delivers them.
    this.scheduleUpdate();
    return this;
  }

  offUpdate(): this {
    this.observer = undefined;
    return this;
  }

  onFlush(flushObserver: () => void): this {
    this.flushObserver = flushObserver;
    return this;
  }

  destroy() {
    this.destroyed = true;
    this.clearScheduledUpdate();
  }

  reset() {
    this.clearScheduledUpdate();
    this.buffer = [];
    this.outgoing = 0;
    this.outgoingId = undefined;
    this.outgoingPayload = undefined;
    this.lastSentClock = null;
  }

  /**
   * Clears in-flight tracking and loads `update` ahead of anything still buffered,
   * without firing the observer. The buffered updates are local edits made after
   * `update` was computed: the NCS connect flow detaches the observer between
   * computing the offline diff and the namespace join, so nothing has sent them
   * yet. Discarding them would leave a permanent hole in the server's step stream
   * that every later edit from this client depends on (CNS-36566).
   */
  resetAndLoad(update: Update<Uint8Array<ArrayBuffer>, UpdateMetadata>): void {
    if (this.destroyed) {
      return;
    }

    const buffered = this.buffer;
    this.reset();
    this.buffer = [update, ...buffered];
    debugLog(
      '[StateBridge:resetAndLoad] loaded update ahead of %d buffered item(s): payload bytes=%d, clock=%d',
      buffered.length,
      update.payload.byteLength,
      update.clock,
    );

    // Kept as a rollout signal for CNS-36566: each capture is a reconnect window in
    // which edits would previously have been dropped.
    if (buffered.length > 0) {
      captureException(
        new StateBridgeBufferMergedNotice(
          'StateBridge.resetAndLoad merged in-flight updates into the reconnect payload',
        ),
        {
          tags: {
            captureLocation: 'state-bridge-reset-and-load-merged-buffer',
            eventClassification: EventClassification.LOG,
            customerImpact: CustomerImpact.NONE,
            capabilityTier: CapabilityTier.C2,
          },
          extra: {
            bufferedUpdates: buffered.length,
            incomingPayloadBytes: update.payload.byteLength,
            incomingClock: update.clock,
          },
        },
      );
    }
  }

  retrieve(): SendData<Uint8Array> | null {
    if (this.destroyed) {
      return null;
    }

    this.clearScheduledUpdate();
    // return undefined payload if empty buffer
    if (this.buffer.length < 1) {
      debugLog(
        '[StateBridge:retrieve] buffer is empty — returning null (nothing to send)',
      );
      return null;
    }
    debugLog(
      '[StateBridge:retrieve] merging %d buffer item(s) to send. Individual payload sizes: [%s]',
      this.buffer.length,
      this.buffer.map((u) => u.payload.byteLength).join(', '),
    );
    const updatesToSend = this.buffer.map((update) => ({
      ...update,
      payload: structuredClone(update.payload),
    }));
    this.outgoing = updatesToSend.length;
    this.outgoingId = uuidv4();
    const mergedUpdate = mergeUpdate(updatesToSend);
    const sessionId = toWireSessionId(currentSessionId.sessionId);
    const updateToSend = {
      name: 'update',
      payload: encodeMessageV2({
        type: MessageType.Update,
        data: {
          data: mergedUpdate.payload,
          origin: getUpdateMessageOrigin(mergedUpdate.origin),
          previousYjsClock: this.lastSentClock,
          currentYjsClock: mergedUpdate.clock,
          ...(sessionId === undefined ? {} : { sessionId }),
        },
      }),
      id: this.outgoingId,
    };
    this.lastSentClock = mergedUpdate.clock;
    this.outgoingPayload = mergedUpdate.payload;
    return updateToSend;
  }

  confirm(_confirmed: SendData<Uint8Array>, id?: string): Promise<void> {
    if (this.destroyed) {
      return Promise.resolve();
    }

    // check the id to make sure it matches
    if (id !== undefined && id === this.outgoingId) {
      debugLog(
        '[StateBridge:confirm] ✅ id matched (%s), removing %d outgoing item(s), remaining buffer: %d',
        id,
        this.outgoing,
        this.buffer.length - this.outgoing,
      );
      if (this.outgoingPayload) {
        this.confirmedPayloadObserver?.(this.outgoingPayload);
      }
      // remove the first N elements from the buffer, where N is the number of updates batched in the latest send
      this.buffer = this.buffer.slice(this.outgoing);
      this.outgoing = 0;
      this.outgoingId = undefined;
      this.outgoingPayload = undefined;
      this.scheduleUpdate();
    } else if (id !== undefined) {
      debugWarn(
        '[StateBridge:confirm] ❌ id MISMATCH — expected=%s, got=%s — triggering mismatch handler',
        this.outgoingId,
        id,
      );
      // id was provided but doesn't match — signal that state is out of sync
      this.mismatchObserver?.();
    }
    return Promise.resolve();
  }

  onConfirmedPayload(observer: (payload: Uint8Array<ArrayBuffer>) => void): this {
    this.confirmedPayloadObserver = observer;
    return this;
  }

  onMismatch(observer: () => void): this {
    this.mismatchObserver = observer;
    return this;
  }

  private scheduleUpdate() {
    if (
      this.destroyed ||
      this.batchTimer ||
      this.buffer.length === 0 ||
      this.outgoing > 0
    ) {
      return;
    }

    this.batchTimer = setTimeout(
      this.flushPendingBatch,
      typeof this.timeout === 'number' ? this.timeout : this.timeout(),
    );
  }

  private flushPendingBatch = () => {
    this.clearScheduledUpdate();

    if (this.destroyed || this.buffer.length === 0 || this.outgoing > 0) {
      return;
    }

    this.observer?.(mergeUpdate(this.buffer));
  };

  private clearScheduledUpdate() {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = undefined;
    }
  }
}
